import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { planningUserTaskService } from "../../../Core/Api/services";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { ManualTaskPlanningResourceType } from "../../../Core/Api/Types/manual-task-planning-resource-type";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "react-i18next";

export type ModalCreateOrUpdateResourceElementProps = {
  mode: "create" | "update";
  availableResources: DbOrderManualTaskPlanningResourceInfos[];
  resourceToUpdate?: DbOrderManualTaskPlanningResourceInfos;
};

const parentalityMatrix: Record<ManualTaskPlanningResourceType, ManualTaskPlanningResourceType[]> = {
  Room: [
    ManualTaskPlanningResourceType.Hardware,
    ManualTaskPlanningResourceType.Software,
    ManualTaskPlanningResourceType.Other,
  ],
  Hardware: [ManualTaskPlanningResourceType.Software, ManualTaskPlanningResourceType.Other],
  Software: [ManualTaskPlanningResourceType.Other],
  Other: [],
};

export type ModalCreateOrUpdateResourceProps = CommonModalProps<ModalCreateOrUpdateResourceElementProps>;

const ModalCreateOrUpdateResource = React.forwardRef<any, ModalCreateOrUpdateResourceProps>((props, ref) => {
  const { t } = useTranslation();
  const { mode, availableResources, resourceToUpdate, closeModal } = props;

  const enqueueSnackbar = useCustomSnackbar();

  const formId = "modal-create-resource";
  const isFormValid = useAppSelector(GetFormIsValid(formId));

  const [name, setName] = useState("");
  const [type, setType] = useState<ManualTaskPlanningResourceType>(ManualTaskPlanningResourceType.Room);
  const [building, setBuilding] = useState("");
  const [floor, setFloor] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [bindToPlatform, setBindToPlatform] = useState(true);
  const [calendar, setCalendar] = useState(true);
  const [attachedResources, setAttachedResources] = useState<DbOrderManualTaskPlanningResourceInfos[]>([]);

  const handleCreateResource = () => {
    planningUserTaskService
      .CreateResource({
        bindToPlatform,
        name,
        type,
        building,
        calendar,
        floor,
        hwResourceIds: attachedResources.map((resource) => resource.id),
        quantity,
      })
      .then((response) => {
        if (response.success) {
          enqueueSnackbar(t("alert:success.successfullyCreatedResource", { objectId: response.objectId }), "success");
          closeModal();
        } else enqueueSnackbar(t("alert:error.unableToCreateResource", { message: response.message }), "error");
      });
  };

  const handleUpdateResource = () => {
    if (!resourceToUpdate) {
      enqueueSnackbar(t("alert:error.resourceNotFound"), "error");
      return;
    }
    planningUserTaskService
      .UpdateResource(resourceToUpdate.id, {
        bindToPlatform,
        name,
        type,
        building,
        calendar,
        floor,
        hwResourceIds: attachedResources.map((resource) => resource.id),
        quantity,
      })
      .then((response) => {
        if (response.success) {
          enqueueSnackbar(t("alert:success.successfullyUpdatedResource", { objectId: response.objectId }), "success");
          closeModal();
        } else enqueueSnackbar(t("alert:error.unableToCreateResource", { message: response.message }), "error");
      });
  };

  useEffect(() => {
    if (mode === "update" && resourceToUpdate) {
      setName(resourceToUpdate.name);
      setType(resourceToUpdate.type);
      setBuilding(resourceToUpdate.location.building);
      setFloor(resourceToUpdate.location.floor);
      setQuantity(resourceToUpdate.quantity || 0);
      setBindToPlatform(resourceToUpdate.platformId !== undefined && resourceToUpdate.platformId !== null);
      setCalendar(resourceToUpdate.calendar);
      setAttachedResources(availableResources.filter((r) => resourceToUpdate.resourceIds.includes(r.id)));
    }
  }, []);

  return (
    <Modal
      title={t("modal:title.createNewResource")}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={closeModal}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={mode === "create" ? handleCreateResource : handleUpdateResource}
            disabled={!isFormValid}
          >
            {mode === "create" ? t("button:createResource") : t("button:updateResource")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <FormInputText
          validationProps={{
            formId: formId,
            fieldId: "name",
            required: true,
            initialState: mode === "update",
            initialValue: name,
          }}
          label={t("common:name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <FormInputSelect
          validationProps={{
            formId: formId,
            fieldId: "type",
            required: true,
            initialState: mode === "update",
            initialValue: type,
          }}
          stackProps={{}}
          label={t("common:type")}
          value={type}
          onChange={(event) => setType(event.target.value as ManualTaskPlanningResourceType)}
        >
          {Object.values(ManualTaskPlanningResourceType).map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </FormInputSelect>
        <FormInputText
          validationProps={{
            formId: formId,
            fieldId: "building",
            required: true,
            initialState: mode === "update",
            initialValue: building,
          }}
          label={t("common:building")}
          value={building}
          onChange={(event) => setBuilding(event.target.value)}
        />
        <FormInputNumber
          validationProps={{
            formId: formId,
            fieldId: "floor",
            required: true,
            initialState: true,
            initialValue: floor,
          }}
          stackProps={{}}
          label={t("common:floor")}
          value={floor}
          onChange={(event) => setFloor(Number(event.target.value))}
        />
        <FormInputNumber
          validationProps={{
            formId: formId,
            fieldId: "quantity",
            required: true,
            positive: true,
            initialState: true,
            initialValue: quantity,
          }}
          stackProps={{}}
          label={t("common:quantity")}
          value={quantity}
          onChange={(event) => setQuantity(Number(event.target.value))}
        />
        <Stack spacing={1}>
          <InputLabel>{t("field:label.attachedResources")}</InputLabel>
          <Autocomplete
            popupIcon={<ExpandMoreRoundedIcon />}
            value={attachedResources}
            options={availableResources.filter((r) => parentalityMatrix[type].includes(r.type))}
            getOptionLabel={(option) => option.name}
            multiple
            onChange={(_, value) => setAttachedResources(value)}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                placeholder={t("field:placeholder.addResources")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Stack>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={bindToPlatform} onChange={(_, checked) => setBindToPlatform(checked)} />}
            label={t("field:label.attachedToYourPlatformOnly")}
          />
          <FormControlLabel
            control={<Checkbox checked={calendar} onChange={(_, checked) => setCalendar(checked)} />}
            label={t("field:label.useOnCalendarOrScheduler")}
          />
        </FormGroup>
      </Stack>
    </Modal>
  );
});

export default ModalCreateOrUpdateResource;
